import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { authenticateUser } from '../../store/auth/duck';
import { abandonStuckSession } from '../../store/user/duck';
import { usePrevious } from '../../utils/hooks';
import { Button, CircularProgress, Typography } from '@mui/material';
import { switchLanguage } from '../../utils/locale';
import { selectedLocale } from '../../store/locale/action';
import { injectIntl } from 'react-intl';
import { ERROR, LOADING } from '../../constants/phase';
import { Network } from '@capacitor/network';

const BACKOFF_MS = [1000, 3000, 7000];
const MAX_AUTO_RETRIES = 3;

const ContainerStyle = {
  position: 'relative',
  height: '100vh',
  overflow: 'hidden'
};

const ProgressContainerStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  textAlign: 'center'
};

export const Auth = props => {
  const {
    initialAuthDone,
    authenticateUser,
    abandonStuckSession,
    userDetails,
    selectedLocale,
    authenticationPhase,
    authRetryCount,
    intl
  } = props;

  const previousInitialAuthDone = usePrevious(initialAuthDone);
  const timers = useRef([]);
  const [exhausted, setExhausted] = useState(false);
  const [leaving, setLeaving] = useState(false);

  const clearTimers = () => {
    timers.current.forEach(id => clearTimeout(id));
    timers.current = [];
  };

  useEffect(() => {
    if (previousInitialAuthDone !== false && initialAuthDone === false) {
      authenticateUser();
    }
  }, [initialAuthDone, previousInitialAuthDone, authenticateUser]);

  useEffect(() => {
    if (initialAuthDone || authenticationPhase !== ERROR) {
      return undefined;
    }
    if ((authRetryCount || 0) >= MAX_AUTO_RETRIES) {
      setExhausted(true);
      return undefined;
    }
    setExhausted(false);
    const delay = BACKOFF_MS[Math.min(authRetryCount || 0, BACKOFF_MS.length - 1)];
    const id = setTimeout(() => authenticateUser(), delay);
    timers.current.push(id);
    return () => clearTimeout(id);
  }, [authenticationPhase, authRetryCount, initialAuthDone, authenticateUser]);

  useEffect(() => {
    const onOnline = () => {
      if (!initialAuthDone) {
        authenticateUser();
      }
    };
    window.addEventListener('online', onOnline);
    let cancelled = false;
    let networkHandle;
    Promise.resolve()
      .then(() =>
        Network.addListener('networkStatusChange', status => {
          if (status && status.connected) {
            onOnline();
          }
        })
      )
      .then(handle => {
        if (cancelled) {
          if (handle && typeof handle.remove === 'function') {
            handle.remove();
          }
          return;
        }
        networkHandle = handle;
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      window.removeEventListener('online', onOnline);
      if (networkHandle && typeof networkHandle.remove === 'function') {
        networkHandle.remove();
      }
      clearTimers();
    };
  }, [authenticateUser, initialAuthDone]);

  useEffect(() => {
    if (userDetails.language) {
      switchLanguage(selectedLocale, userDetails.language);
    }
  }, [userDetails]);

  return (
    <div style={ContainerStyle}>
      <div style={ProgressContainerStyle}>
        {exhausted && authenticationPhase !== LOADING ? (
          <div>
            <Typography>{intl.formatMessage({ id: 'Unable to verify your session' })}</Typography>
            <Button
              color="primary"
              disabled={leaving}
              onClick={() => {
                setExhausted(false);
                authenticateUser();
              }}
              style={{ marginTop: 12, display: 'block', marginLeft: 'auto', marginRight: 'auto' }}
            >
              {intl.formatMessage({ id: 'Retry' })}
            </Button>
            <Button
              color="primary"
              disabled={leaving}
              onClick={() => {
                setLeaving(true);
                clearTimers();
                abandonStuckSession();
              }}
              style={{ marginTop: 4, display: 'block', marginLeft: 'auto', marginRight: 'auto' }}
            >
              {intl.formatMessage({ id: 'Go to Login' })}
            </Button>
          </div>
        ) : (
          <CircularProgress />
        )}
      </div>
    </div>
  );
};

const container = connect(
  state => ({
    initialAuthDone: state.auth.initialAuthDone,
    isAuthenticated: state.auth.isAuthenticated,
    authenticatedUser: state.auth.authenticatedUser,
    authenticationError: state.auth.authenticationError,
    authenticationPhase: state.auth.authenticationPhase,
    authRetryCount: state.auth.authRetryCount,
    userDetails: state.user.userDetails,
    lang: state.locale.lang
  }),
  {
    authenticateUser,
    abandonStuckSession,
    selectedLocale
  }
)(Auth);

export default injectIntl(container);
